import type { FC, ReactNode } from 'react';
import type {
  CapacityCategory,
  GasType,
  NetworkPoint,
  PhysicalUnit,
} from 'src/apis/monolith/types';
import { Card } from 'src/components/data-display/card';
import { DataItems, DataList } from 'src/components/data-display/data-list';
import { Direction } from 'src/components/data-display/direction';
import { Divider } from 'src/components/dividers';
import { OperatorLogo } from 'src/components/domain-specifics/operator-logo';
import { Link } from 'src/components/navigation/link';
import { Heading } from 'src/components/text/heading';
import { capacityLabels } from 'src/pages/reporting/capacity';
import { labelsToOptions } from 'src/utils/labels-to-options';

export const gasTypeLabels: Record<GasType, string> = {
  // oxlint-disable-next-line prisma/title-case
  H_GAS: 'H-gas',
  // oxlint-disable-next-line prisma/title-case
  L_GAS: 'L-gas',
};

const gasTypeOptions = labelsToOptions(gasTypeLabels);

export const physicalUnitLabels: Record<PhysicalUnit, string> = {
  K_WH_H: 'kWh/h',
  K_WH_D: 'kWh/d',
  // CM_3_H: 'Cm³/h',
  M3: 'm³',
  M3_H: 'Cm³/h',
};

type Props = {
  networkPoint: NetworkPoint;
  capacityCategory0: CapacityCategory;
  capacityCategory1?: CapacityCategory;
  /** @deprecated Remove this when the TAG-7505 evaluation phase is over. */
  withUnderlinedLinks?: boolean;
};

export const NetworkPointCard: FC<Props> = ({
  networkPoint,
  capacityCategory0,
  capacityCategory1,
  withUnderlinedLinks,
}) => {
  return (
    <Card data-testid="network-point-card">
      <Heading mode="title" size="md">
        Network Point
      </Heading>

      {networkPoint.bundled ? (
        <div
          className={`
            grid items-center gap-md
            legacy:gap-[1rem]
          `}
        >
          {/* BUNDLE: */}
          <DataList>
            <DataItems>
              <span>Name</span>
              <span>
                <Link
                  to={`/transport/network-points/${networkPoint.id}`}
                  mode={withUnderlinedLinks ? 'default-underlined' : 'default'}
                >
                  <strong>{networkPoint.name}</strong>
                </Link>{' '}
                <Direction networkPoint={networkPoint} />
              </span>

              <span>Identifier</span>
              <span>{networkPoint.identifier}</span>

              <span>Flow Unit</span>
              <span>{physicalUnitLabels[networkPoint.physicalUnit]}</span>

              {/* there is also a gasType1, but you can only have a bundle if both are the same */}
              <span>Type of Gas</span>
              <span>{gasTypeLabels[networkPoint.gasType0]}</span>
            </DataItems>
          </DataList>

          <Divider />

          {/* EXIT: */}

          <OperatorAndPoint
            operator={
              <Link
                to={`/market-information/players/tsos/${networkPoint.tso0Id}`}
                mode={withUnderlinedLinks ? 'default-underlined' : 'default'}
              >
                <div
                  className={`
                    grid items-center gap-md text-center
                    legacy:gap-[1rem]
                  `}
                >
                  <OperatorLogo
                    src={networkPoint.tso0LogoSmallUrl}
                    name={networkPoint.tso0Name}
                  />
                  <p>{networkPoint.tso0Name}</p>
                </div>
              </Link>
            }
            point={
              <div
                className={`
                  grid items-center gap-md
                  legacy:gap-[1rem]
                `}
              >
                <p>
                  <Link
                    to={`/transport/network-points/${networkPoint.id0}`}
                    mode={
                      withUnderlinedLinks ? 'default-underlined' : 'default'
                    }
                  >
                    <strong>{networkPoint.name0}</strong>
                  </Link>{' '}
                  <Direction
                    networkPoint={{
                      direction: 'EXIT',
                      marketAreaName: networkPoint.marketArea0,
                    }}
                  />
                </p>

                <DataList>
                  <DataItems>
                    <span>Identifier</span>
                    <span>{networkPoint.identifier0}</span>

                    <span>Capacity Category</span>
                    <span>{capacityLabels[capacityCategory0]}</span>
                  </DataItems>
                </DataList>
              </div>
            }
          />

          <Divider />

          {/* ENTRY: */}

          <OperatorAndPoint
            operator={
              <Link
                to={`/market-information/players/tsos/${networkPoint.tso1Id}`}
                mode={withUnderlinedLinks ? 'default-underlined' : 'default'}
              >
                <div
                  className={`
                    grid items-center gap-md text-center
                    legacy:gap-[1rem]
                  `}
                >
                  <OperatorLogo
                    src={networkPoint.tso1LogoSmallUrl}
                    name={networkPoint.tso1Name}
                  />

                  <p>{networkPoint.tso1Name}</p>
                </div>
              </Link>
            }
            point={
              <div
                className={`
                  grid items-center gap-md
                  legacy:gap-[1rem]
                `}
              >
                <p>
                  <Link
                    to={`/transport/network-points/${networkPoint.id1}`}
                    mode={
                      withUnderlinedLinks ? 'default-underlined' : 'default'
                    }
                  >
                    <strong>{networkPoint.name1}</strong>
                  </Link>{' '}
                  <Direction
                    networkPoint={{
                      direction: 'ENTRY',
                      marketAreaName: networkPoint.marketArea1,
                    }}
                  />
                </p>

                <DataList>
                  <DataItems>
                    <span>Identifier</span>
                    <span>{networkPoint.identifier1}</span>

                    {capacityCategory1 && (
                      <>
                        <span>Capacity Category</span>
                        <span>{capacityLabels[capacityCategory1]}</span>
                      </>
                    )}
                  </DataItems>
                </DataList>
              </div>
            }
          />
        </div>
      ) : (
        <OperatorAndPoint
          operator={
            <Link
              to={`/market-information/players/tsos/${networkPoint.tso0Id}`}
              mode={withUnderlinedLinks ? 'default-underlined' : 'default'}
            >
              <div
                className={`
                  grid items-center gap-md text-center
                  legacy:gap-[1rem]
                `}
              >
                <OperatorLogo
                  src={networkPoint.tso0LogoSmallUrl}
                  name={networkPoint.tso0Name}
                />
                <p>{networkPoint.tso0Name}</p>
              </div>
            </Link>
          }
          point={
            <div
              className={`
                grid items-center gap-md
                legacy:gap-[1rem]
              `}
            >
              <p>
                <Link
                  to={`/transport/network-points/${networkPoint.id}`}
                  mode={withUnderlinedLinks ? 'default-underlined' : 'default'}
                >
                  <strong>{networkPoint.name}</strong>
                </Link>{' '}
                <Direction networkPoint={networkPoint} />
              </p>

              <DataList>
                <DataItems>
                  <span>Identifier</span>
                  <span>{networkPoint.identifier}</span>

                  <span>Flow Unit</span>
                  <span>{physicalUnitLabels[networkPoint.physicalUnit]}</span>

                  <span>Type of Gas</span>
                  <span>{gasTypeLabels[networkPoint.gasType0]}</span>

                  <span>Capacity Category</span>
                  <span>{capacityLabels[capacityCategory0]}</span>
                </DataItems>
              </DataList>
            </div>
          }
        />
      )}
    </Card>
  );
};

export const OperatorAndPoint: FC<{
  operator: ReactNode;
  point: ReactNode;
}> = ({ operator, point }) => {
  return (
    <div
      className={`
        grid grid-cols-1 items-center gap-md
        lg:grid-cols-[3fr_2fr]
        legacy:gap-[1rem]
      `}
    >
      <div
        className={`
          order-1
          lg:order-2
        `}
      >
        {operator}
      </div>
      <div
        className={`
          order-2
          lg:order-1
        `}
      >
        {point}
      </div>
    </div>
  );
};
